import { useEffect, useRef, useState } from 'react';
import { Mail, Phone, Search } from 'lucide-react';

import { api } from '@/core/api/queries';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { ReportPageLayout } from '@/shared/components/layouts';
import { EditionSelector } from '@/shared/components/tables';
import { useReportPage } from '@/shared/hooks';
import { columnBuilder } from '@/shared/utils/column-builder';
import { hrefTelefon } from '@/shared/utils/contact-ecotic';
import { useAuthProviderV2, useRoutesProvider } from '@/providers/provider-hooks';
import { formatZi } from '@/domains/tickets/utils/tier-state';
import {
    etichetaStare,
    etichetaVechime,
    localitate,
    numarRecipiente,
    numeFel,
    stareRand,
    zileDeLa,
    type FiltruFel,
    type FiltruStare,
} from '../utils/stare-rand-admin';
import { useRecipienteMutations } from '../hooks/use-recipiente-mutations';
import type {
    FiltruRaportRecipiente,
    WasteContainerRequestEditDto,
    WasteContainerRequestReportDto,
} from '../api/waste-container-requests.api';

/** Chip-urile de filtru, in ordinea de pe ecran. */
const CHIPURI: { stare: FiltruStare; eticheta: string }[] = [
    { stare: 'toate', eticheta: 'Toate' },
    { stare: 'IN_ASTEPTARE', eticheta: 'În așteptare' },
    { stare: 'CONFIRMATA', eticheta: 'Confirmate' },
    { stare: 'LIVRATA', eticheta: 'Livrate' },
];

/** Chip-urile de fel. «Ambele» = fara filtru pe fel (owner, 2026-09-22). */
const CHIPURI_FEL: { fel: FiltruFel; eticheta: string }[] = [
    { fel: 'ambele', eticheta: 'Ambele' },
    { fel: 'RECIPIENT_DBA', eticheta: 'Recipient DBA' },
    { fel: 'MINIRECIPIENTE', eticheta: 'Minirecipiente DBA' },
];

/** Cautarea pleaca la server dupa o pauza de tastare, nu la fiecare litera. */
const PAUZA_CAUTARE_MS = 300;

type RandRaport = WasteContainerRequestReportDto & FiltruRaportRecipiente;

/**
 * Cererile de recipiente — lista de lucru a adminului (SP-04, partea a doua).
 *
 * 🔴 NU e un raport obisnuit, e o LISTA DE LUCRU. Un raport se citeste; o lista de
 * lucru se **goleste**. De-aia ordinea implicita (fara sortare pe coloana) e
 * **crescatoare** dupa data cererii (cea mai veche sus), invers fata de celelalte
 * rapoarte — o da backendul. Vechimea („de N zile") sta langa data.
 *
 * Din 29.09 pe tiparul comun (`useReportPage` + `ReportPageLayout`): raportul e
 * paginat, iar filtrul pe stare/fel, cautarea, sortarea (doar coloanele din lista
 * alba a backendului), contoarele chip-urilor si exportul Excel se fac pe server.
 * Exportul e deschis si pentru LOGISTIC (owner). Ecranul e folosit pe desktop; pe
 * mobil ramane rezumatul standard.
 *
 * 🔴 Ce NU are voie sa apara, verificat de S1 pe sursa: buton de respingere sau
 * de stergere (nu exista flux, si stergerea ar sterge dovada ca scoala a cerut),
 * statusuri fara sursa, kilograme, termene de livrare.
 */
export default function RecipienteAdminPage() {
    const { report, adapters, handleExport, editions, currentEdition, changeEdition, setPageable } =
        useReportPage<RandRaport>({
            queryHook: api.wasteContainerRequests.report,
            exportEndpoint: '/api/waste-container-requests/report',
            exportFilename: 'comenzi_recipiente',
            withEditions: true,
            initialPageSize: 50,
        });
    const editionId = currentEdition?.id;
    const contoare = api.wasteContainerRequests.contoare.useQuery({
        variables: { editionId },
        enabled: !!editionId,
    }).data;
    const { marcheazaProcesata, marcheazaLivrata, editeaza } = useRecipienteMutations();
    const { userInfo } = useAuthProviderV2();
    const { ROLES } = useRoutesProvider();
    // SE-18: editarea e DBEST/ADMIN — nu LOGISTIC, verificat pe sursa backend
    // (`editeaza()` in `WasteContainerRequestController.java`). Oglinda UI a
    // gate-ului real: backendul ramane sursa de adevar, aici doar ascundem ce
    // oricum ar pica 403.
    const poateEdita = userInfo?.role === ROLES.DBEST || userInfo?.role === ROLES.ADMIN;
    // ECOTIC, 29.09: confirmarea (NOUA -> PROCESATA) e tot DBEST/ADMIN — backendul da
    // 403 la LOGISTIC pe `/procesata`, iar lui LOGISTIC nici nu-i ajung cererile NOUA.
    const poateConfirma = poateEdita;

    /**
     * Filtrul porneste pe TOATE cererile, si asta e o decizie, nu un implicit
     * nescris: filtrul initial al raportului e gol.
     *
     * 🔴 Dupa marcare, randul RAMANE in lista, cu «Confirmată pe <data>» — *asta* e
     * confirmarea ca actiunea a avut loc. Cu un filtru pornit implicit, randul ar
     * disparea exact in clipa marcarii, si singurul feedback ar fi absenta lui: fix
     * intrebarea „am marcat-o, sau am visat?" pe care ecranul exista s-o inchida.
     * Cand adminul alege EL un chip, disparitia e consecinta alegerii lui — cifra
     * de pe chip-ul vecin creste, deci dovada ramane pe ecran.
     *
     * ⚠️ **Conditie pentru cine schimba asta** (ceruta de S1): cine porneste vreodata
     * filtrul implicit trebuie sa inlocuiasca disparitia randului cu o confirmare la
     * fel de durabila. Altfel schimbarea nu adauga un filtru — sterge dovada.
     */
    const filtruStare: FiltruStare = report.filter.stareRand ?? 'toate';
    const filtruFel: FiltruFel = report.filter.fel ?? 'ambele';
    // In ref, nu in dependinte: `report` e un obiect nou la fiecare randare, iar debounce-ul
    // s-ar reporni la nesfarsit.
    const raportRef = useRef(report);
    raportRef.current = report;

    const [cautare, setCautare] = useState('');
    useEffect(() => {
        const t = setTimeout(() => {
            const valoare = cautare.trim() || undefined;
            // Fara schimbare, fara apel: `updateFilter` intoarce paginarea la prima pagina.
            if (valoare !== raportRef.current.filter.cautare) raportRef.current.updateFilter({ cautare: valoare });
        }, PAUZA_CAUTARE_MS);
        return () => clearTimeout(t);
    }, [cautare]);
    // «Resetează filtrele» din bara comuna goleste filtrul raportului; caseta de cautare il urmeaza.
    useEffect(() => {
        if (!report.filter.cautare) setCautare('');
    }, [report.filter.cautare]);

    /** Ce rand are confirmarea deschisa. Confirmarea e IN CELULA, nu un dialog —
     *  aceeasi coloana serveste si «Confirmă», si «Marchează livrată»: un rand
     *  are mereu o SINGURA actiune posibila, data de starea lui curenta. */
    const [deConfirmat, setDeConfirmat] = useState<string | null>(null);
    /** Randul cu dialogul de editare deschis. Un dialog ADEVARAT, nu in celula:
     *  sapte campuri nu incap intr-o celula de tabel. */
    const [deEditat, setDeEditat] = useState<WasteContainerRequestReportDto | null>(null);

    const seMarcheaza = marcheazaProcesata.isPending || marcheazaLivrata.isPending;
    // Relativ la „acum", la fiecare randare: pagina statuta peste miezul noptii se
    // corecteaza la urmatoarea actiune, fara timer.
    const acum = new Date();

    const confirma = (r: WasteContainerRequestReportDto) => {
        if (!r.id) return;
        // SE-18: NOUA -> Confirmă (PROCESATA); PROCESATA -> Marchează livrată (LIVRATA).
        const mutatie = r.stare === 'PROCESATA' ? marcheazaLivrata : marcheazaProcesata;
        mutatie.mutate(r.id, { onSuccess: () => setDeConfirmat(null) });
    };

    const coloane = columnBuilder<RandRaport>()
        .custom({
            key: 'stare',
            label: 'Stare',
            sortable: true,
            // Data se muta SUB pastila, si are voie sa se rupa: «Confirmată pe 2 septembrie 2026»
            // pe un rand facea coloana de 260 px si impingea «Acțiune» afara din ecran la 1280.
            render: (_, r) => {
                const [titlu, zi] = etichetaStare(r).split(' pe ');
                return (
                    <div className="flex flex-col items-start gap-0.5">
                        <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-light px-3 py-1 text-smaller text-dark">
                            {titlu}
                        </span>
                        {zi && <span className="text-smaller text-gray-600">pe {zi}</span>}
                    </div>
                );
            },
        })
        .custom({
            key: 'scoala',
            label: 'Școală',
            sortable: true,
            cardRole: 'title',
            // Codul SE sub nume: sunt scoli cu acelasi nume in judete diferite, iar
            // adminii lucreaza oricum pe cod.
            render: (_, r) => (
                <div className="min-w-28">
                    <p className="m-0 font-semibold text-dark">{r.schoolName ?? '—'}</p>
                    {r.codSe && <p className="m-0 text-smaller text-gray-600">Cod SE {r.codSe}</p>}
                </div>
            ),
            cardRender: (_, r) => r.schoolName ?? '—',
        })
        .custom({
            key: 'judet',
            // „Localitate", nu „Județ / Localitate": antetul e `text-nowrap` in `DataTable`,
            // deci eticheta lunga fixa latimea minima a coloanei.
            label: 'Localitate',
            sortable: true,
            // Adresa de livrare e informatie PRINCIPALA: e o copie facuta la cerere, deci
            // poate diferi de fisa scolii, si acolo se livreaza.
            render: (_, r) => {
                const strada = [r.deliveryAddress?.strada, r.deliveryAddress?.numar].filter(Boolean).join(' ');
                return (
                    <div>
                        <p className="m-0 text-dark">{localitate(r) || '—'}</p>
                        {strada && <p className="m-0 text-smaller text-gray-600">{strada}</p>}
                    </div>
                );
            },
        })
        /* «Ce a cerut» + «Cerut pe» intr-o singura coloana, «Cerere» (owner, 2026-09-22).
           Sortarea e pe DATA — criteriul listei de lucru; felul se alege din chip-uri. */
        .custom({
            key: 'data',
            label: 'Cerere',
            sortable: true,
            // Data lipsa lasa randul de data gol; nu punem „azi" si nici data importului.
            render: (_, r) => {
                const ts = typeof r.timestamp === 'string' ? r.timestamp : null;
                const zi = formatZi(ts ?? undefined);
                return (
                    <div>
                        <p className="m-0 text-dark">{numeFel(r) ?? '—'}</p>
                        {zi && (
                            <p className="m-0 text-smaller text-gray-600">
                                <span className="whitespace-nowrap">{zi}</span>
                                {' · '}
                                <span className="whitespace-nowrap">{etichetaVechime(zileDeLa(ts, acum))}</span>
                            </p>
                        )}
                    </div>
                );
            },
        })
        .custom({
            key: 'numar',
            label: '# recipiente',
            sortable: true,
            cardRole: 'value',
            // `align: 'right'` aduce si cifre tabulare (`DataTable`). Recipientul DBA = 1 (owner, 2026-09-22).
            align: 'right',
            render: (_, r) => numarRecipiente(r) ?? <span className="text-gray-600">—</span>,
            cardRender: (_, r) => numarRecipiente(r) ?? '—',
        })
        /* 🔴 DOUA persoane, si niciuna n-are ambele canale: cea de livrare are doar
           telefon, solicitantul doar email. Doua coloane etichetate dupa ROL, nu
           „Contact 1 / Contact 2". Netrunchiate, deliberat. */
        .action('livrare', 'Livrare', (_, r) => (
            <div className="flex flex-col gap-0.5">
                <span className="text-dark">{r.contactPersonName || '—'}</span>
                {/* `hrefTelefon`, nu `tel:${...}` brut: telefonul poate avea spatii sau
                    cratime, iar un spatiu intr-un URL rupe linkul. */}
                {r.contactPersonPhone &&
                    (hrefTelefon(r.contactPersonPhone) ? (
                        <a
                            href={hrefTelefon(r.contactPersonPhone)}
                            className="inline-flex items-center gap-1 whitespace-nowrap font-semibold text-magenta"
                        >
                            <Phone className="size-3.5" aria-hidden />
                            {r.contactPersonPhone}
                        </a>
                    ) : (
                        <span className="text-dark">{r.contactPersonPhone}</span>
                    ))}
            </div>
        ))
        .action('solicitant', 'Cine a cerut', (_, r) => (
            <div className="flex flex-col gap-0.5">
                <span className="text-dark">{r.solicitantNume || '—'}</span>
                {r.solicitantEmail && (
                    /* Rupt doar inainte de `@` (`<wbr>`), nu cu `break-all`. */
                    <a
                        href={`mailto:${r.solicitantEmail}`}
                        className="font-semibold text-magenta break-words"
                    >
                        <Mail className="mr-1 inline size-3.5 align-text-bottom" aria-hidden />
                        {r.solicitantEmail.split('@')[0]}
                        {r.solicitantEmail.includes('@') && (
                            <>
                                <wbr />@{r.solicitantEmail.split('@').slice(1).join('@')}
                            </>
                        )}
                    </a>
                )}
            </div>
        ))
        /* Actiunea principala. Nu „aproba/respinge": scoala are dreptul la recipiente.
           «Confirmă»/«Marchează livrată» afirma un fapt din lumea reala, deci se
           confirma — dar IN CELULA, nu cu dialog modal. SE-18: doi pasi
           (NOUA -> PROCESATA -> LIVRATA); «Editează» doar cat cererea e NOUA. */
        .action('actiune', 'Acțiune', (_, r) => {
            if (stareRand(r) === 'livrata') return <span className="text-gray-600">—</span>;
            const confirmata = r.stare === 'PROCESATA';
            if (!confirmata && !poateConfirma) return <span className="text-gray-600">—</span>;
            if (deConfirmat === r.id) {
                return (
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="text-small text-dark">Sigur?</span>
                        <Button type="button" size="sm" onClick={() => confirma(r)} aria-disabled={seMarcheaza}>
                            {seMarcheaza ? 'Se marchează…' : 'Da'}
                        </Button>
                        <Button
                            type="button"
                            size="sm"
                            onClick={() => setDeConfirmat(null)}
                            className="border border-input bg-white font-semibold text-dark hover:bg-light"
                        >
                            Nu
                        </Button>
                    </div>
                );
            }
            return (
                <div className="flex flex-wrap items-center gap-2">
                    <Button type="button" size="sm" onClick={() => setDeConfirmat(r.id ?? null)}>
                        {confirmata ? 'Marchează livrată' : 'Confirmă'}
                    </Button>
                    {poateEdita && !confirmata && (
                        <Button
                            type="button"
                            size="sm"
                            onClick={() => setDeEditat(r)}
                            className="border border-input bg-white font-semibold text-dark hover:bg-light"
                        >
                            Editează
                        </Button>
                    )}
                </div>
            );
        })
        .build();

    const filtrat = filtruStare !== 'toate' || filtruFel !== 'ambele' || !!report.filter.cautare;
    // Cheile contoarelor sunt camelCase (`WasteContainerRequestContoareDto`), filtrele sunt enumuri.
    const contorStare: Record<FiltruStare, unknown> = {
        toate: contoare?.total,
        IN_ASTEPTARE: contoare?.peStare?.inAsteptare,
        CONFIRMATA: contoare?.peStare?.confirmata,
        LIVRATA: contoare?.peStare?.livrata,
    };
    const contorFel: Record<FiltruFel, unknown> = {
        ambele: contoare?.total,
        RECIPIENT_DBA: contoare?.peFel?.recipientDba,
        MINIRECIPIENTE: contoare?.peFel?.minirecipiente,
    };
    // Spec-ul spune `bigint`, pe sarma e `number`; `String()` le acopera pe amandoua.
    const cifra = (v: unknown) => (v == null ? '–' : String(v));

    const chip = (activ: boolean) =>
        cn(
            'inline-flex min-h-9 items-center gap-1.5 rounded-full border px-3 text-small font-semibold transition-colors',
            activ ? 'border-dark bg-dark text-white' : 'border-input bg-white text-dark hover:bg-light'
        );

    return (
        /* `px-2` pe celule, doar pe pagina asta: `ecotic-table-body-cell` are 24 px pe
           fiecare parte, iar la 1280, cu sidebar-ul si bara de controale comuna, «Acțiune»
           ajungea afara din ecran (masurat 29.09: cu `px-3`, tot 86 px peste).
           Tot de-aia `whitespace-normal`: utilitatea pune `nowrap` pe celula, deci nici
           numele scolii, nici emailul nu se puteau rupe. `contents` nu adauga cutie in
           lantul de inaltime al layoutului; `DataTable` si `main.css` raman neatinse. */
        <div className="contents [&_td]:whitespace-normal [&_td]:px-2 [&_th]:px-2">
            <ReportPageLayout
                title="Comenzi recipiente DBA"
                report={report}
                adapters={adapters}
                handleExport={handleExport}
                columns={coloane}
                entityLabel="comenzi"
                unitLabel="recipiente"
                emptyMessage={
                    filtrat
                        ? 'Nicio cerere nu corespunde filtrului.'
                        : 'Nicio comandă de recipiente în ediția aceasta.'
                }
                extraControls={
                    /* Trei randuri, fiecare cat continutul lui: editie + cautare, apoi starea,
                       apoi felul. `lg:shrink-0`, pentru ca in bara comuna controalele sunt un element
                       flex langa `ReportDetails` (`w-full`), care altfel le strange pana la un chip
                       pe rand. */
                    <div className="flex flex-col gap-2 lg:shrink-0">
                        <div className="flex flex-wrap items-end gap-3">
                            <EditionSelector
                                editions={editions ?? []}
                                currentEditionId={editionId}
                                onChange={(id) => changeEdition?.(id)}
                            />
                            <div className="relative w-full sm:w-80">
                                <Search
                                    className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-gray-600"
                                    aria-hidden
                                />
                                <Input
                                    type="search"
                                    value={cautare}
                                    onChange={(e) => setCautare(e.target.value)}
                                    placeholder="Școală, cod SE, localitate, email, telefon"
                                    aria-label="Caută cereri"
                                    className="pl-9"
                                />
                            </div>
                            {report.currentSort && (
                                <Button
                                    type="button"
                                    onClick={() => setPageable((p) => ({ ...p, sort: [], page: 0 }))}
                                    className="border border-input bg-white font-semibold text-dark hover:bg-light"
                                >
                                    Ordinea implicită
                                </Button>
                            )}
                        </div>
                        {/* Chip-urile sunt filtrul SI numaratoarea: cifrele vin din backend, pe
                            toata editia, nu din pagina curenta si nici din ce a ramas dupa cautare. */}
                        <div role="group" aria-label="Filtru după stare" className="flex flex-wrap items-center gap-2">
                            {CHIPURI.map((c) => (
                                <button
                                    key={c.stare}
                                    type="button"
                                    aria-pressed={filtruStare === c.stare}
                                    onClick={() =>
                                        report.updateFilter({ stareRand: c.stare === 'toate' ? undefined : c.stare })
                                    }
                                    className={chip(filtruStare === c.stare)}
                                >
                                    {c.eticheta}
                                    <span className="tabular-nums">{cifra(contorStare[c.stare])}</span>
                                </button>
                            ))}
                        </div>
                        <div role="group" aria-label="Filtru după ce a cerut" className="flex flex-wrap items-center gap-2">
                            <span className="text-small text-gray-600">Ce a cerut:</span>
                            {CHIPURI_FEL.map((c) => (
                                <button
                                    key={c.fel}
                                    type="button"
                                    aria-pressed={filtruFel === c.fel}
                                    onClick={() => report.updateFilter({ fel: c.fel === 'ambele' ? undefined : c.fel })}
                                    className={chip(filtruFel === c.fel)}
                                >
                                    {c.eticheta}
                                    <span className="tabular-nums">{cifra(contorFel[c.fel])}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                }
            />

            <DialogEditare
                rand={deEditat}
                seSalveaza={editeaza.isPending}
                onClose={() => setDeEditat(null)}
                onSalveaza={(dto) => {
                    if (!deEditat?.id) return;
                    editeaza.mutate({ id: deEditat.id, dto }, { onSuccess: () => setDeEditat(null) });
                }}
            />
        </div>
    );
}

interface DialogEditareProps {
    /** `null` = dialog inchis. */
    rand: WasteContainerRequestReportDto | null;
    seSalveaza: boolean;
    onClose: () => void;
    onSalveaza: (dto: WasteContainerRequestEditDto) => void;
}

/**
 * Editare rapida, DOAR pe o cerere NOUA (SE-18) — backendul refuza cu 409 dupa
 * confirmare, deci butonul care il deschide nu apare pentru alte stari (coloana
 * «Acțiune»). Dialog ADEVARAT (nu in celula, ca «Confirmă»): campurile nu incap
 * intr-o celula de tabel, si o editare gresita nu e la fel de reversibila ca o confirmare.
 */
function DialogEditare({ rand, seSalveaza, onClose, onSalveaza }: DialogEditareProps) {
    const [cantitate, setCantitate] = useState('');
    const [contactPersonName, setContactPersonName] = useState('');
    const [contactPersonPhone, setContactPersonPhone] = useState('');
    const [judet, setJudet] = useState('');
    const [oras, setOras] = useState('');
    const [strada, setStrada] = useState('');
    const [numar, setNumar] = useState('');

    // Precompletare din randul curent, de fiecare data cand se deschide alt rand.
    useEffect(() => {
        if (!rand) return;
        setCantitate(rand.cantitate != null ? String(rand.cantitate) : '');
        setContactPersonName(rand.contactPersonName ?? '');
        setContactPersonPhone(rand.contactPersonPhone ?? '');
        setJudet(rand.deliveryAddress?.judet ?? '');
        setOras(rand.deliveryAddress?.oras ?? '');
        setStrada(rand.deliveryAddress?.strada ?? '');
        setNumar(rand.deliveryAddress?.numar ?? '');
    }, [rand]);

    if (!rand) return null;
    const areCantitate = rand.fel === 'MINIRECIPIENTE';

    const salveaza = () => {
        const cantitateNumar = Number(cantitate);
        const dto: WasteContainerRequestEditDto = {
            ...(areCantitate && cantitate !== '' && Number.isFinite(cantitateNumar)
                ? { cantitate: cantitateNumar }
                : {}),
            ...(contactPersonName ? { contactPersonName } : {}),
            ...(contactPersonPhone ? { contactPersonPhone } : {}),
            // `AddressDto.judet`/`.oras` sunt obligatorii pe TIP — un obiect de adresa
            // fara ele n-ar fi valid, deci il trimitem doar cand ambele sunt completate.
            ...(judet && oras ? { deliveryAddress: { judet, oras, strada, numar } } : {}),
        };
        onSalveaza(dto);
    };

    return (
        <Dialog open onOpenChange={(deschis) => { if (!deschis) onClose(); }}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Editează cererea</DialogTitle>
                </DialogHeader>

                <div className="flex flex-col gap-3">
                    {areCantitate && (
                        <div className="space-y-1">
                            <Label htmlFor="editare-cantitate">Câte minirecipiente</Label>
                            <Input
                                id="editare-cantitate"
                                type="number"
                                min={1}
                                inputMode="numeric"
                                value={cantitate}
                                onChange={(e) => setCantitate(e.target.value)}
                            />
                        </div>
                    )}
                    <div className="space-y-1">
                        <Label htmlFor="editare-nume-contact">Persoana de contact</Label>
                        <Input
                            id="editare-nume-contact"
                            value={contactPersonName}
                            onChange={(e) => setContactPersonName(e.target.value)}
                        />
                    </div>
                    <div className="space-y-1">
                        <Label htmlFor="editare-telefon-contact">Telefonul persoanei de contact</Label>
                        <Input
                            id="editare-telefon-contact"
                            type="tel"
                            inputMode="tel"
                            value={contactPersonPhone}
                            onChange={(e) => setContactPersonPhone(e.target.value)}
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                            <Label htmlFor="editare-judet">Județ</Label>
                            <Input id="editare-judet" value={judet} onChange={(e) => setJudet(e.target.value)} />
                        </div>
                        <div className="space-y-1">
                            <Label htmlFor="editare-oras">Oraș</Label>
                            <Input id="editare-oras" value={oras} onChange={(e) => setOras(e.target.value)} />
                        </div>
                        <div className="space-y-1">
                            <Label htmlFor="editare-strada">Stradă</Label>
                            <Input id="editare-strada" value={strada} onChange={(e) => setStrada(e.target.value)} />
                        </div>
                        <div className="space-y-1">
                            <Label htmlFor="editare-numar">Număr</Label>
                            <Input id="editare-numar" value={numar} onChange={(e) => setNumar(e.target.value)} />
                        </div>
                    </div>
                </div>

                <DialogFooter>
                    <Button
                        type="button"
                        onClick={onClose}
                        className="min-h-11 border border-input bg-white font-semibold text-dark hover:bg-light"
                    >
                        Renunță
                    </Button>
                    <Button type="button" onClick={salveaza} aria-disabled={seSalveaza} className="min-h-11">
                        {seSalveaza ? 'Se salvează…' : 'Salvează'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
